/**
 * IndependantGameTab.jsx
 * 
 * Composant d'administration permettant la gestion :
 * - des jeux
 * - des infos liées aux jeux, point de départ d'un jeu dans une langue données
 * - des questions (qui contient la gestion des réponses)
 * 
 * @module IndependantGameTab
 */

import React, { useState, useEffect } from 'react';
import styles from './IndependantGameTab.module.scss';
import { DEBUG, PATH } from '../../../utils/constants';

// Onglets
import GamesTab from './GamesTab/GamesTab';
import QuestionsIndeTab from './QuestionsIndeTab/QuestionsIndeTab';
import ReponsesIndeTab from './ReponsesIndeTab/ReponsesIndeTab';

// APIS 
import { getAllGames, createGame, updateGame, deleteGame, regenCode} from '../../../utils/api/gamesAPI';
import { getAllGameInfos, createGameInfos, updateGameInfos, deleteGameInfos, toggleGameInfos } from '../../../utils/api/gameInfosAPI';
import { getAllQuestionsByGame, createQuestion, updateQuestion, updateQuestionImage, updateQuestionOptions, deleteQuestion } from '../../../utils/api/questionsIndeAPI';

const debug = DEBUG;

/**
 * Composant AdminPanel
 * Interface d'administration complète pour gérer la hiérarchie des jeux atypiques (independants).
 * @component
 * @returns {JSX.Element} L'interface administrateur
 */
function IndependantGameTab() {

  // ---------------------------------STATES--------------------------------------------
  const [activeTab, setActiveTab] = useState('games');

  const [games, setGames] = useState([]);
  const [allGameInfos, setAllGameInfos] = useState([]);
  const [selectedGameInfos, setSelectedGameInfos] = useState(null);
  const [questions, setQuestions] = useState([]);

  const [selectedQuestion, setSelectedQuestion] = useState(null);

  // ---------------------------------EFFECTS--------------------------------------------
  /**
   * Récupère tous les jeux au montage
   */
  useEffect(() => {
      const fetchData = async () => {
      try {
          const [fetchedGames, fetchedGameInfos] = await Promise.all([
          getAllGames(),
          getAllGameInfos(),
          ]);
          const sortedGames = [...fetchedGames].sort((a, b) => a.nom.localeCompare(b.nom));
          setGames(sortedGames);
          setAllGameInfos(fetchedGameInfos);
      } catch (error) {
          if(debug) console.error('Erreur lors du chargement des données', error);
      }
      };
      fetchData();
  }, []);

  /**
   * Trie les jeux par nom
   */
  useEffect(() => {
      setGames(games.sort((a, b) => a.nom.localeCompare(b.nom)));
  }, [games]);


  // ---------------------------------EVENTS--------------------------------------------
  // Fonctions CRUD (jeux, langues, questions, etc.)

  // ---------------------- GameTab -----------------------------
  /**
   * Crée un nouveau jeu
   * @param {string} gameName 
   * @returns {Object|boolean} nouveau jeu ou false
   */
  const handleCreateGame = async (gameName) => {
      try {
      const newGame = await createGame(gameName); // Appel à l'API pour créer la région
  
      // Vérifier si la réponse contient bien une région (ajoute une vérification ici si nécessaire)
      if (newGame && newGame.id) {
          // Mettre à jour l'état localement
          newGame.nom = gameName;
          setGames((prev) => [...prev, newGame]);
  
          // Retourner true si la création a réussi
          return newGame;
      } else {
          throw new Error("La réponse ne contient pas un jeu valide.");
          // return false;
      }
      } catch (error) {
      if(debug) console.error("Erreur lors de la création du jeu :", error);
      return false;
      }
  };

    // Séparer la mise à jour de l'image de la mise à jour des données du jeu !!!!!

  /**
   * Met à jour un jeu
   * @param {number} gameId 
   * @param {string} newName 
   * @returns {boolean}
   */

  const handleUpdateGame = async (gameId, payload) => {
    try {
      const sanitized = Object.fromEntries(
        Object.entries(payload).map(([k, v]) => [
          k,
          typeof v === "string" ? (v.trim() === "" ? null : v.trim()) : v
        ])
      );

      // Appel API
      const resp = await updateGame(gameId, sanitized);

      // MAJ locale (optimiste). Si l’API renvoie l’objet jeu, préfère-le.
      const patch = resp?.game ? resp.game : sanitized;

      setGames(prev =>
        prev.map(game =>
          game.id === gameId ? { ...game, ...patch } : game
        )
      );

      return true;
    } catch (error) {
      if (debug) console.error("Erreur mise à jour du jeu :", error);
      return false;
    }
  };


  const handleGameImageUpdated = (gameId, newImagePath) => {
    setGames(prev =>
      prev.map(game =>
        game.id === gameId ? { ...game, image: newImagePath } : game
      )
    );
  };

  /**
   * Régénération du code du jeu
   * @param {number} gameId 
   */
  const handleRegenerateCode = async (gameId) => {
    const res = await regenCode(gameId);
    if (!res.code) throw new Error("Erreur");
    
    // Mets à jour la liste des jeux
    setGames(prev =>
      prev.map(g => g.id === gameId ? { ...g, code: res.code } : g)
    );
    return res.code;
  };

  /**
   * Supprime un jeu + langues associées
   * @param {number} gameId 
   * @returns {boolean}
   */
  const handleDeleteGame = async (gameId) => {
    try {
      const confirmed = window.confirm("Voulez-vous vraiment supprimer ce jeu ? Cette action est irréversible.");
      if (!confirmed) return false;

      await deleteGame(gameId);
      setGames(prev => prev.filter(game => game.id !== gameId));
      // Supprimer également les villages associés de l'état local
      setAllGameInfos(prev => prev.filter(gameInfos => gameInfos.id_game !== gameId));
      return true;
    } catch (error) {
      if(debug) console.error("Erreur suppression du jeu :", error);
      return false;
    }
  };

  /**
   * Crée une langue dans un jeu
   * @param {number} gameId 
   * @param {Object} { lang, intro, outro } 
   */
  const handleCreateGameInfos = async (gameId, { lang, intro, outro }) => {
    try {
      const newId = await createGameInfos(intro, outro, lang, gameId, 0 );
      if (newId.id) {
        const newGameInfos = {
          id: newId.id, // ID retourné par l'API
          intro: intro, 
          outro: outro,
          lang: lang,
          active: 0, 
          id_game: gameId, // ID du jeu sélectionné
        };

        if(debug) console.log("Nouvelle langue créée :", newGameInfos);
        
        setAllGameInfos((prevGameInfos) => [...prevGameInfos, newGameInfos]);
      }
    } catch (error) {
      if (debug) console.error("Erreur ajout gameInfo :", error);
    }
  };

  /**
   * Met à jour une langue
   * @param {number} parcoursId 
   * @param {string} newName 
   * @param {string} intro 
   * @param {string} outro 
   * @param {string} lang 
   * @returns {boolean}
   */
  const handleUpdateGameInfos = async (gameInfosId, {intro, outro, lang}) => {
    try {
      await updateGameInfos(gameInfosId, intro, outro, lang);
      setAllGameInfos(prev => 
        prev.map(gameInfos => 
          gameInfos.id === gameInfosId ? { ...gameInfos, intro:intro, outro:outro, lang: lang } : gameInfos
        )
      );
      
      // Mettre à jour aussi selectedParcours si nécessaire
      // if (selectedParcours && selectedParcours.id === parcoursId) {
      //   setSelectedParcours(prev => ({ ...prev, nom: newName }));
      // }
      
      return true;
    } catch (error) {
      if(debug) console.error("Erreur mise à jour de la langue :", error);
      return false;
    }
  };

  /**
   * Active/désactive une langue
   * @param {number} parcoursId 
   * @returns {boolean}
   */
  const handleToggleGameInfos = async (id, active) => {
    try {
      await toggleGameInfos(id, active);
      setAllGameInfos(prev => prev.map(p => p.id === id ? { ...p, active } : p));
      setSelectedGameInfos(prev => prev && prev.id === id ? { ...prev, active } : prev);
    } catch (error) {
      console.error("Erreur toggle parcours:", error);
    }
  };

  /**
   * Supprime une langue
   * @param {number} gameInfosId 
   * @returns {boolean}
   */
  const handleDeleteGameInfos = async (gameInfosId) => {
    try {
      const confirmed = window.confirm("Supprimer cette langue ? Cette action est définitive.");
      if (!confirmed) return false;

      const verif = await deleteGameInfos(gameInfosId);
      if(verif.success) {
        setAllGameInfos(prev => prev.filter(gameInfos => gameInfos.id !== gameInfosId));
        return true;
      }
    } catch (error) {
      if(debug) console.error("Erreur suppression de la langue :", error);
      return false;
    }
  };


  /**
   * Sélectionne une langue d'un jeu et récupère ses questions
   * @param {Object} gameInfos 
   */
  // Charge l'écran de la gestion d'un seul parcours + liste des questions 
  const handleGameInfosSelect = async (gameInfos) => {
    setSelectedGameInfos(gameInfos);

    try {
      const questions = await getAllQuestionsByGame(gameInfos.id);
      if (Array.isArray(questions)) {
        setQuestions(questions);
      } else {
        // Si c’est une erreur mais sans throw (ex: 404 avec message), on vide la liste
        setQuestions([]);
      }
    } catch (error) {
      // Si ton API throw vraiment l'erreur, on gère ici
      if (error.response && error.response.status === 404) {
        if(debug) console.warn("Aucune question pour ce parcours");
        setQuestions([]);
      } else {
        if(debug) console.error("Erreur récupération des questions :", error);
      }
    }

    setActiveTab('questions');
  };

  // ---------------------- QuestionIndeTab -----------------------------

  /**
   * Crée une nouvelle question dans un parcours
   * @param {number} gameId 
   * @param {Object} data 
   */
  const handleCreateQuestion = async (gameId, data) => {
    try {
      const newQuestionId = await createQuestion(gameId, data.libelle, data.type);
      if (newQuestionId && newQuestionId.id) {
        // Créer un nouvel objet question avec les données et l'ID retourné
        const newQuestion = {
          id: newQuestionId.id,
          libelle: data.libelle,
          type: data.type,
          helper: "",
          // Si vous avez besoin de stocker les noms des fichiers après upload
          img: null,
          img_helper: null,
          id_game: gameId,
          placement: questions.length + 2
        };

        // A étudier pour le placement :
        // const maxPlacement = Math.max(...questions.map(q => q.placement || 0), 0);
        // placement: maxPlacement + 1
        
        // Mettre à jour l'état local des questions
        setQuestions(prev => [...prev, newQuestion]);
      }
    } catch (error) {
      if(debug) console.error('Erreur création de question', error);
    }
  };

  /**
   * Met à jour une question
   * @param {number} questionId 
   * @param {Object} data 
   * @returns {boolean}
   */
  const handleUpdateQuestion = async (questionId, data) => {
    try {
      // const result = await updateQuestion(questionId, data.libelle, data.type, data.helper, data.img_helper, data.delete_image);
      const result = await updateQuestion(questionId, data.libelle, data.type, data.helper, data.img_helper ?? undefined, data.delete_img_helper ?? false);
      if (result.success) { // Vérifiez le format exact de la réponse de votre API
        // Mettre à jour l'état local des questions
        setQuestions(prev => prev.map(question => 
          question.id === questionId ? {
            ...question,
            libelle: data.libelle,
            type: data.type,
            helper: data.helper,
            ...(data.img_helper instanceof File && result.img_helper ? { img_helper: result.img_helper } : data.delete_img_helper ? { img_helper: null } : {}),
          } : question
        ));
        return true;
      }
      return false;
    } catch (error) {
      console.error('Erreur mise à jour de question', error);
      return false;
    }
  };


  /**
   * Met à jour les options d'une question
   * @param {number} questionId 
   * @param {Object} data 
   * @returns {boolean}
   */
  const handleUpdateQuestionImage = async (questionId, data) => {
    try {
      const result = await updateQuestionImage(questionId, data.img ?? undefined, data.delete_img ?? false);
      if (result.success) { 
        setQuestions(prev => prev.map(question => 
          question.id === questionId ? {
            ...question,
            ...(data.img instanceof File && result.img ? { img: result.img } : data.delete_img ? { img: null } : {}),
          } : question
        ));
        return true;
      }
      return false;
    } catch (error) {
      console.error('Erreur mise à jour de question', error);
      return false;
    }
  };


  /**
   * Met à jour les options d'une question
   * @param {number} questionId 
   * @param {Object} data 
   * @returns {boolean}
   */
  const handleUpdateQuestionOptions = async (questionId, data) => {
    try {
      const result = await updateQuestionOptions(questionId, data.cle_indice, data.message, data.img_indice ?? undefined, data.delete_img_indice ?? false);
      if (result.success) { 
        setQuestions(prev => prev.map(question => 
          question.id === questionId ? {
            ...question,
            cle_indice: data.cle_indice,
            message: data.message,
            ...(data.img_indice instanceof File && result.img_indice ? { img_indice: result.img_indice } : data.delete_img_indice ? { img_indice: null } : {}),
          } : question
        ));
        return true;
      }
      return false;
    } catch (error) {
      console.error('Erreur mise à jour de question', error);
      return false;
    }
  };

  /**
   * Supprime une question
   * @param {number} questionId 
   * @returns {boolean}
   */
  const handleDeleteQuestion = async (questionId) => {
    try {
      const result = await deleteQuestion(questionId);
      if (result) { 
        setQuestions(prev => prev.filter(question => question.id !== questionId));
        return true;
      }
      return false;
    } catch (error) {
      console.error('Erreur suppression de question', error);
      return false;
    }
  };

  const handleQuestionInfosSelect = (question) => {
    setSelectedQuestion(question);
    setActiveTab('reponses');
  }


  /**
   * Change l’onglet actif
   * @param {string} tabName 
   */
  const handleTabChange = (tabName) => {
    setActiveTab(tabName);
  };

  // ---------------------------------FONCTIONS--------------------------------------------
  /**
   * Retourne le contenu du tab actif
   * @returns {JSX.Element}
   */
  const renderTabContent = () => {
    switch (activeTab) {
      case 'games':
        return (
          <GamesTab
            games={games}
            gameInfos={allGameInfos}
            onCreateGame={handleCreateGame}
            onUpdateGame={handleUpdateGame}
            onDeleteGame={handleDeleteGame}
            onGameImageUpdated={handleGameImageUpdated}
            onCreateGameInfos={handleCreateGameInfos}
            onUpdateGameInfos={handleUpdateGameInfos}
            onDeleteGameInfos={handleDeleteGameInfos}
            onToggleGameInfosActive={handleToggleGameInfos}
            onRegenerateCode={handleRegenerateCode}
            onGameInfosSelect={handleGameInfosSelect}
          />
        );
      case 'questions':
      return (
        <QuestionsIndeTab
          gameInfos={selectedGameInfos}
          questions={questions}// Envoyer uniquement les questions de la langue
          onCreateQuestion={handleCreateQuestion}
          onUpdateQuestion={handleUpdateQuestion}
          onUpdateQuestionImage={handleUpdateQuestionImage}
          onUpdateQuestionOptions={handleUpdateQuestionOptions}
          onDeleteQuestion={handleDeleteQuestion}
          onTabChange={handleTabChange}
          onQuestionInfosSelect={handleQuestionInfosSelect}
        />
      );
      case 'reponses':
      return (
        <ReponsesIndeTab
          question={selectedQuestion}
          onCloseTab={handleTabChange}
        />
      );
    //   default:
    //     return <ErrorTab />;
    }
  };

  // ---------------------------------LAYOUT--------------------------------------------
  return (
    <div className={styles.adminPanel}>

      <div className={styles.tabContent}>
        {renderTabContent()}
      </div>
    </div>
  );
}

export default IndependantGameTab;
